import { and, asc, desc, eq, isNotNull, like } from "drizzle-orm";
import { block, page, page_version, type PowerSyncDrizzleDB } from "@apunte-digital/powersync";
import { livePageFilters } from "../live/filters";

/** Backlinks panel row: a page linking to the current one. */
export interface BacklinkRow {
  id: string;
  title: string | null;
  icon: string | null;
}

/**
 * All pages visible locally (owned + shared with me).
 * PowerSync only synchronizes accessible rows, so there is no need to filter
 * by user_id: the local database is already the user's "scope". Pages in
 * the trash (deleted_at) remain OUT of all normal views.
 */
export function getPagesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(page)
    .where(and(...livePageFilters()))
    .orderBy(asc(page.sort_key), asc(page.created_at));
}

/**
 * Page details by id. It does NOT filter the trash: the route decides what
 * to show (a page in the trash is treated as not found).
 */
export function getPageQuery(db: PowerSyncDrizzleDB, pageId: string) {
  return db.select().from(page).where(eq(page.id, pageId)).limit(1);
}

/**
 * Blocks of a page, in document order.
 */
export function getPageBlocksQuery(db: PowerSyncDrizzleDB, pageId: string) {
  return db
    .select()
    .from(block)
    .where(eq(block.page_id, pageId))
    .orderBy(asc(block.sort_key), asc(block.created_at));
}

/** Lightweight row for analytics: only what is needed to count words/links. */
export interface BlockStatRow {
  page_id: string | null;
  text: string | null;
  content: string | null;
  updated_at: string | null;
}

/**
 * All blocks visible locally, in their lightweight form for analytics
 * (word count, link map, activity by date). Local-first:
 * runs on SQLite, with no round-trip. Excludes pages in the trash.
 */
export function getAllBlocksQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({
      page_id: block.page_id,
      text: block.text,
      content: block.content,
      updated_at: block.updated_at,
    })
    .from(block)
    .innerJoin(page, eq(block.page_id, page.id))
    .where(and(...livePageFilters()));
}

/**
 * Backlinks derived locally, with no extra table: pages whose blocks
 * contain a wikiLink node to `pageId`. The JSON of a wikiLink always
 * serializes `"pageId":"<uuid>"` (without spaces), so a LIKE over the
 * content is exact in practice — and runs on local SQLite for free.
 */
export function getBacklinksQuery(db: PowerSyncDrizzleDB, pageId: string) {
  return db
    .selectDistinct({ id: page.id, title: page.title, icon: page.icon })
    .from(block)
    .innerJoin(page, eq(block.page_id, page.id))
    .where(and(like(block.content, `%"pageId":"${pageId}"%`), ...livePageFilters()))
    .orderBy(asc(page.title));
}

/**
 * Basic page search by title.
 */
export function searchPagesQuery(db: PowerSyncDrizzleDB, term: string) {
  return db
    .select()
    .from(page)
    .where(and(like(page.title, `%${term}%`), ...livePageFilters()))
    .orderBy(asc(page.title))
    .limit(50);
}

/**
 * Trash (L2): soft-deleted pages, newest first.
 * FLAT list (including subpages dragged by their parent): each row can
 * be restored or permanently deleted separately.
 */
export function getTrashedPagesQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(page)
    .where(isNotNull(page.deleted_at))
    .orderBy(desc(page.deleted_at), asc(page.title));
}

/**
 * LOCAL version history for a page (PowerSync bucket bounded to the
 * sync window). UUIDv7 id ⇒ descending order = newest first.
 * Deep history is requested on demand through the paginated API.
 */
export function getPageVersionsQuery(db: PowerSyncDrizzleDB, pageId: string) {
  return db
    .select()
    .from(page_version)
    .where(eq(page_version.page_id, pageId))
    .orderBy(desc(page_version.id));
}
